import React from 'react';
import styled from 'styled-components';

import { STATE_OPTIONS } from 'constants/states';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { Location } from 'models/flexible/Location';
import type { AdminLocationIndexFields } from 'services/api/fields/admin/location';

const LOCATION_INCLUDES = ['groupsCount'] as const;

type LocationRow = Location<AdminLocationIndexFields<(typeof LOCATION_INCLUDES)[number]>>;
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexLocations, adminLocationsQueryKeys, archiveLocation } from 'services/api/admin/locations';
import { colors } from 'styles/theme/colors';
import DeactivateIcon from 'svg/DeactivateIcon';
import PencilIcon from 'svg/PencilIcon';

import AssociatedGroupsSlideout from './AssociatedGroupsSlideout';

const stateFilterOptions = STATE_OPTIONS.map((s) => ({ id: s.value, name: s.value }));

const config: AdminListPageConfig<LocationRow> = {
  title: 'Locations',
  queryKey: adminLocationsQueryKeys.index,
  fetchFn: (params, signal) => adminIndexLocations(params, signal, [...LOCATION_INCLUDES]),
  includes: ['groups_count'],
  fetchParams: {
    defaultSort: 'name asc',
    pageSize: 10,
  },
  searchPlaceholder: 'Search Locations',
  showResultCount: true,

  addButton: {
    label: 'Add Location',
    path: '/admin/locations/new',
    permission: 'adminGroupCreate',
  },

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'locations', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
    {
      key: 'state',
      label: 'State',
      type: 'dropdown',
      staticOptions: stateFilterOptions,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
  ],

  columns: [
    {
      id: 'name',
      header: 'Location',
      render: (row) => <BoldText>{row.name || '\u2014'}</BoldText>,
    },
    {
      id: 'clientName',
      header: 'Client Name',
      sortable: false,
      render: (row) => row.client?.name || '\u2014',
    },
    {
      id: 'address',
      header: 'Address',
      sortable: false,
      render: (row) => row.formattedAddress || '\u2014',
    },
    {
      id: 'groupsCount',
      header: 'Associated Groups',
      sortable: false,
      render: (row, ctx) => {
        if (!row.groupsCount) return '\u2014';
        return (
          <GroupsCountLink onClick={() => ctx.openSlideout('associatedGroups', row)}>{row.groupsCount}</GroupsCountLink>
        );
      },
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminGroupView',
      onClick: (row, { navigate }) => navigate(`/admin/locations/${row.id}/edit`),
    },
    {
      label: 'Archive',
      icon: <DeactivateIcon width={18} height={18} color={colors.accentRed} />,
      danger: true,
      permission: 'adminGroupEdit',
      visible: (row) => !row.groupsCount,
      onClick: (row, { openModal }) => openModal('archive', row),
    },
  ],

  modals: {
    archive: {
      actionText: 'Archive',
      objType: 'location',
      onConfirm: async (row, ctx) => {
        try {
          await archiveLocation(row.id);
        } catch (e: any) {
          const errors = e?.response?.data?.errors;
          const message = errors?.base?.join(', ') || 'Unable to archive this location.';
          ctx.toast(message);
        }
      },
    },
  },

  slideouts: {
    associatedGroups: {
      render: (row, onClose) => (
        <AssociatedGroupsSlideout locationId={row.id} locationName={row.name} handleClose={onClose} />
      ),
    },
  },
};

export default function Locations() {
  const state = useAdminListPage(config);
  return <AdminListPageRenderer config={config} state={state} />;
}

const BoldText = styled.span`
  font-weight: 700;
`;

const GroupsCountLink = styled.span`
  color: ${({ theme }) => theme.colors.primaryBlue};
  cursor: pointer;
  text-decoration: underline;
`;
